import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppResponse } from '@data/app/response';
import { TaxCredits } from '@data/keyIssues/key-issues';
import { PickListDetail, projectStatus, studios } from '@data/pickList/pick-list';
import { LockBudget } from '@data/preProdBudget/pre-prod-budget';
import { Schedule } from '@data/schedule/schedule';
import { environment } from '@environments/environment';
import { ApiPaths } from '@mpg-core/constants/api-paths.enum';
import { changeDateFormat, changeDateFormatJavaToUi } from '@shared/common-functions/utility';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { InvalidDataNotification, Project, projectAka, ProjectNotification } from './project';


@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  lockPreProd = new BehaviorSubject<boolean>(false);
  
  lockClicked = new BehaviorSubject<boolean>(false);

  selectedProjectData:Project;
  selectedWeekendingDt = new BehaviorSubject<Date|string|null>(null);
  selectedWeekendingDtProjectStatus = "";

  currentProjectReportTab = "";

  currentWeekendingDt:string|Date="";

  activeBudgetGrossNetAmount = new Subject<{gross:string,net:string}>();

  latestPreProdTaxCredit = new BehaviorSubject<TaxCredits[]>([]);
  latestPreProdSchedule = new BehaviorSubject<Schedule[]>([]);

  isProjectUpdated = new BehaviorSubject<boolean>(false);

  projectNotification = new Subject<ProjectNotification>();

  invalidDataNotificationList: InvalidDataNotification[]=[];
  isInvalidDataChanged = new Subject<boolean>();

  isTabChanged = new Subject<boolean>();

  studioList:PickListDetail[]=[];
  statusList:PickListDetail[]=[];

  constructor(private http: HttpClient){
  }

  flushData(){
    this.currentWeekendingDt="";
    this.activeBudgetGrossNetAmount.next({gross:"",net:""});
    this.currentProjectReportTab="";
    this.selectedWeekendingDtProjectStatus="";
  }

  getProjectDetails(projectId:number): Observable<any> {
    return this.http.get<any>(environment.apiUrl+ ApiPaths.getProjectById+projectId.toString()).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  getRecentlyViewedTitlesByUser(mpgpsrUserId:number): Observable<any> {
    return this.http.get<any>(environment.apiUrl+ ApiPaths.getRecentlyViewedTitlesByUser+"/"+mpgpsrUserId.toString()).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  // saveRecentViewedTitles(payload:any): Observable<any> {
  //   return this.http.post<any>(environment.apiUrl+ ApiPaths.saveRecentViewedTitles, payload ).pipe(
  //     map((res: AppResponse) => {
  //       return res;
  //     })
  //   );
  // }

  saveProject(payload:Project): Observable<any> {
    payload.weekendingDt = payload.weekendingDt? changeDateFormatJavaToUi(payload.weekendingDt,"yyyy-mm-dd"): "";
    payload.changeOverStatusDate = payload.changeOverStatusDate? changeDateFormatJavaToUi(payload.changeOverStatusDate,"yyyy-mm-dd"):null;
    payload.flexiLockedDate = payload.flexiLockedDate? changeDateFormatJavaToUi(payload.flexiLockedDate,"yyyy-mm-dd"):null;
    
    return this.http.post<any>(environment.apiUrl+ ApiPaths.saveProject, payload ).pipe(
      map((res: AppResponse) => {
        payload.weekendingDt = payload.weekendingDt? changeDateFormatJavaToUi(payload.weekendingDt,"yyyy/mm/dd"): "";
        payload.changeOverStatusDate = payload.changeOverStatusDate? changeDateFormatJavaToUi(payload.changeOverStatusDate,"yyyy/mm/dd"):null;
        payload.flexiLockedDate = payload.flexiLockedDate? changeDateFormatJavaToUi(payload.flexiLockedDate,"yyyy/mm/dd"):null;
        
        return res;
      })
    );
  }

  updateProject(payload:any): Observable<any> {
    return this.http.post<any>(environment.apiUrl+ ApiPaths.updateProject, payload ).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  searchProject(query:string): Observable<any> {
    return this.http.get<any>(environment.apiUrl+ ApiPaths.searchProject+"/"+query).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  changeProjectStatus(payload:Project): Observable<any> {
    return this.http.post<any>(environment.apiUrl+ ApiPaths.changeProjectStatus,payload).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  lockBudget(payload:LockBudget){
    return this.http.post<any>(environment.apiUrl+ ApiPaths.lockPreProdBudget, payload ).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  getPreProdDate(){
    if(!this.selectedProjectData.changeOverStatusDate)
      return null;
    else{

      let cDate = new Date(changeDateFormatJavaToUi(this.selectedProjectData.changeOverStatusDate,"yyyy/mm/dd"));
      // cDate.setDate(cDate.getDate() - 7);
      // console.log(this.selectedProjectData.changeOverStatusDate);
      // console.log(cDate);
      
      return changeDateFormat(cDate,"yyyy-mm-dd");
    }
  }

  previewPsr(payload:any): Observable<any> {
    
    return this.http.post(environment.reportUrl+ ApiPaths.psrReport, payload ,{observe:'response',responseType:'blob'});
  }

  getProjectAka(projectId:number){
    return this.http.get<any>(environment.apiUrl+ ApiPaths.getProjectAka).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  saveProjectAka(payload:projectAka[]){
    return this.http.post<any>(environment.apiUrl+ ApiPaths.saveProjectAka,payload).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  deleteProjectAka(akaId:number){
    let params = new HttpParams()
      .set('akaId', akaId);
    return this.http.delete<any>(environment.apiUrl+ ApiPaths.deleteProjectAka,{params}).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  addProductionEntity(payload:any){
    return this.http.post<any>(environment.apiUrl+ ApiPaths.addProductionEntity,payload).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  copyPostProductionSchedulesToActualsForWED(payload:any){
    return this.http.post<any>(environment.apiUrl+ ApiPaths.copyPostProductionSchedulesToActualsForWED,payload).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  getStudioNameUsingId(id:number|null|undefined){
    if(!id)
      return '';

    studios.subscribe(r=>{ 
      this.studioList=r; 
    });

    for(let s of this.studioList){
      if(s.picklistDetailId==id)
        return s.value;
    }

    return '';
  }


  getStatusUsingId(id:number){
    projectStatus.subscribe(r=>{ 
      this.statusList=r; 
    });
    for(let s of this.statusList){
      if(s.picklistDetailId==id)
        return s.value;
    }
    return '';
  }

  generateWeeklyVarianceHistory(payload:any){
    return this.http.post(environment.apiUrl+ApiPaths.generateWeeklyVarianceHistory , payload, {
      responseType:'blob',
      headers: new HttpHeaders().append("Content-Type", "application/json")
    });
    
  }

  deleteProject(payload:any): Observable<any> {
    return this.http.post<any>(environment.apiUrl+ ApiPaths.deleteProject, payload ).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

  checkIfBudgetPresentByProject(projectId:number): Observable<any> {
    return this.http.get<any>(environment.apiUrl+ ApiPaths.checkIfBudgetPresentByProject+"/"+projectId).pipe(
      map((res: AppResponse) => {
        return res;
      })
    );
  }

}
